import React from "react";
import colourToHex from "convert-css-color-name-to-hex";
import ReactSlider from "react-slider";
import { ChoiceList, Heading, OptionList, Stack, Tag } from "@shopify/polaris";
import { FiltersProps, FiltersSelected, Filters as ReactifySearchFilters, useFilters } from "@usereactify/search";

type FilterListProps = NonNullable<React.ComponentProps<NonNullable<FiltersProps["renderFilterList"]>>>;

export function Filters() {
  return (
    <>
      <FilterHeader />
      <ReactifySearchFilters
        renderFilterList={FilterList}
        renderFilterRange={FilterTypeRange}
        renderFilterSlider={FilterTypeSlider}
      />
    </>
  );
}

function FilterHeader() {
  const filtersHook = useFilters();

  return (
    <FiltersSelected
      render={(renderProps) => (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "0 15px 10px" }}>
            <Heading>Filters</Heading>
          </div>
          {renderProps.selectedFilters.length > 0 ? (
            <fieldset className="Polaris-BoxList Polaris-ChoiceList">
              <Stack spacing="tight">
                {renderProps.selectedFilters.map((selectedFilter) => {
                  const filter = (filtersHook.filters ?? []).find((filter) => filter.handle === selectedFilter.key);
                  if (!filter) {
                    return null;
                  }

                  if (filter.displayType === "single" && filter.displayView === "range") {
                    return (
                      <Tag
                        key={selectedFilter.key}
                        onRemove={() =>
                          renderProps.handleRemove(selectedFilter.key, [
                            (selectedFilter.value as { label: string }).label,
                          ])
                        }
                      >
                        {`${selectedFilter.label}: `}
                        {(selectedFilter.value as { label: string }).label}
                      </Tag>
                    );
                  }
                  if (filter.displayType === "single" && filter.displayView !== "range") {
                    return (
                      <Tag
                        key={selectedFilter.key}
                        onRemove={() => renderProps.handleRemove(selectedFilter.key, [selectedFilter.value as string])}
                      >
                        {`${selectedFilter.label}: `}
                        {selectedFilter.value as string}
                      </Tag>
                    );
                  }
                  if (filter.displayType === "multi" && filter.displayView === "range") {
                    return (
                      <Stack key={selectedFilter.key} spacing="tight">
                        {(selectedFilter.value as Array<{ label: string }>).map((value) => (
                          <Tag
                            key={value.label}
                            onRemove={() => renderProps.handleRemove(selectedFilter.key, [value.label])}
                          >
                            {`${selectedFilter.label}: `}
                            {value.label}
                          </Tag>
                        ))}
                      </Stack>
                    );
                  }
                  if (filter.displayType === "multi" && filter.displayView !== "range") {
                    return (
                      <Stack key={selectedFilter.key} spacing="tight">
                        {(selectedFilter.value as Array<string>).map((value) => (
                          <Tag key={value} onRemove={() => renderProps.handleRemove(selectedFilter.key, [value])}>
                            {`${selectedFilter.label}: `}
                            {value}
                          </Tag>
                        ))}
                      </Stack>
                    );
                  }
                  if (filter.displayType === "slider") {
                    return (
                      <Tag key={selectedFilter.key} onRemove={() => renderProps.handleRemove(selectedFilter.key, [])}>
                        {`${selectedFilter.label}: `}
                        {`${filter.displaySliderPrefix}${
                          (selectedFilter.value as any)[0]
                        } to ${filter.displaySliderPrefix}${(selectedFilter.value as any)[1]}`}
                      </Tag>
                    );
                  }
                })}
              </Stack>
            </fieldset>
          ) : null}
        </>
      )}
    />
  );
}

function FilterList(props: FilterListProps) {
  const { filter } = props;

  if ("list" === filter.displayView) {
    return <FilterTypeList {...props} />;
  }

  if ("check" === filter.displayView) {
    return <FilterTypeCheckbox {...props} />;
  }

  if ("swatch" === filter.displayView) {
    return <FilterTypeSwatch {...props} />;
  }

  if ("box" === filter.displayView) {
    return <FilterTypeBox {...props} />;
  }

  return <FilterTypeCheckbox {...props} />;
}

function FilterTypeList({ filter, filterListProps: { options, handleChange } }: FilterListProps) {
  const selected = React.useMemo(() => options.filter(({ checked }) => checked).map(({ key }) => key), [options]);

  const handleOptionChange = React.useCallback(
    (selectedItems: string[]) => {
      const newSelected =
        selectedItems.find((item: string) => !selected.includes(item)) ||
        selected.find((item) => !selectedItems.includes(item));

      if (newSelected) {
        handleChange(newSelected);
      }
    },
    [selected]
  );

  return (
    <OptionList
      role="listbox"
      title={filter.name}
      onChange={handleOptionChange}
      options={options.map(({ key, checked }) => ({
        value: key,
        label: `${key && filter.settingsUppercase === true ? key.toUpperCase() : key}`,
        active: checked,
      }))}
      selected={selected}
    />
  );
}

function FilterTypeCheckbox({ filter, filterListProps: { options, handleChange } }: FilterListProps) {
  const selected = React.useMemo(() => options.filter(({ checked }) => checked).map(({ key }) => key), [options]);

  const handleOptionChange = React.useCallback(
    (selectedItems: string[]) => {
      const newSelected =
        selectedItems.find((item: string) => !selected.includes(item)) ||
        selected.find((item) => !selectedItems.includes(item));

      if (newSelected) {
        handleChange(newSelected);
      }
    },
    [selected]
  );

  return (
    <ChoiceList
      title={filter.name}
      onChange={handleOptionChange}
      allowMultiple={filter.displayType === "multi"}
      choices={options.map(({ key, checked }) => ({
        value: key,
        label: `${key && filter.settingsUppercase === true ? key.toUpperCase() : key}`,
        active: checked,
      }))}
      selected={selected}
    />
  );
}

function FilterTypeBox({ filter, filterListProps: { options, handleChange } }: FilterListProps) {
  const selected = React.useMemo(() => options.filter(({ checked }) => checked).map(({ key }) => key), [options]);

  const handleOptionChange = React.useCallback(
    (value: string) => {
      if ("single" === filter.displayType) {
        handleChange(selected[0]);
        handleChange(value);
      } else {
        handleChange(value);
      }
    },
    [selected]
  );

  return (
    <fieldset className="Polaris-BoxList Polaris-ChoiceList">
      <legend className="Polaris-BoxList__Title">{filter.name}</legend>
      <Stack spacing="tight">
        {options.map(({ key, checked }) => (
          <a
            key={key}
            title={key && filter.settingsUppercase === true ? key.toUpperCase() : key}
            className={`Polaris-BoxList__Box${checked ? " active" : ""}`}
            onClick={() => handleOptionChange(key)}
          >
            {key && filter.settingsUppercase === true ? key.toUpperCase() : key}
          </a>
        ))}
      </Stack>
    </fieldset>
  );
}

function FilterTypeSwatch({ filter, filterListProps: { options, handleChange } }: FilterListProps) {
  const selected = React.useMemo(() => options.filter(({ checked }) => checked).map(({ key }) => key), [options]);

  const handleOptionChange = React.useCallback(
    (value: string) => {
      if ("single" === filter.displayType) {
        handleChange(selected[0]);
        handleChange(value);
      } else {
        handleChange(value);
      }
    },
    [selected]
  );

  return (
    <fieldset className="Polaris-SwatchList Polaris-ChoiceList">
      <legend className="Polaris-SwatchList__Title">{filter.name}</legend>
      <Stack spacing="tight">
        {options.map(({ key, checked }) => (
          <a
            key={key}
            title={key && filter.settingsUppercase === true ? key.toUpperCase() : key}
            className={`Polaris-SwatchList__Box${checked ? " active" : ""}`}
            style={{ backgroundColor: `${colourToHex(key)}` }}
            onClick={() => handleOptionChange(key)}
          />
        ))}
      </Stack>
    </fieldset>
  );
}

type FilterSliderProps = NonNullable<React.ComponentProps<NonNullable<FiltersProps["renderFilterSlider"]>>>;

function FilterTypeSlider(props: FilterSliderProps) {
  const trackStyleEnds = {
    top: "10px",
    height: "8px",
    background: "rgb(239, 239, 239)",
    border: "1px solid rgb(179, 179, 179)",
    borderRadius: "4px",
  };
  const trackStyleMiddle = {
    ...trackStyleEnds,
    background: "rgb(2, 117, 255)",
    border: "1px solid rgb(74, 117, 187)",
  };

  return (
    <fieldset className="Polaris-Slider Polaris-ChoiceList">
      <legend className="Polaris-Slider__Title">{props.filter.name}</legend>
      <div
        className="rs__filter__slider"
        style={{
          maxWidth: "300px",
          height: "36px",
          margin: "auto",
          paddingTop: "16px",
        }}
      >
        <ReactSlider
          thumbClassName="rs__filter__slider__thumb"
          {...props.reactSliderProps}
          renderTrack={(props, state) => (
            <div
              {...props}
              style={{
                ...(state.index === 1 ? trackStyleMiddle : trackStyleEnds),
                ...props.style,
              }}
            ></div>
          )}
          renderThumb={(props, state) => (
            <div
              {...props}
              style={{
                cursor: "grab",
                background: "rgb(2, 117, 255)",
                top: "6px",
                width: "16px",
                height: "16px",
                borderRadius: "100%",
                outline: "none",
                ...props.style,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "-24px",
                  left: "0",
                }}
              >
                {state.valueNow}
              </span>
            </div>
          )}
        />
      </div>
    </fieldset>
  );
}

type FilterRangeProps = NonNullable<React.ComponentProps<NonNullable<FiltersProps["renderFilterRange"]>>>;

function FilterTypeRange(props: FilterRangeProps) {
  const selected = React.useMemo(
    () => props.filterRangeProps.options.filter(({ checked }) => checked).map(({ key }) => key),
    [props.filterRangeProps.options]
  );

  const handleOptionChange = React.useCallback(
    (selectedItems: string[]) => {
      const newSelected =
        selectedItems.find((item: string) => !selected.includes(item)) ||
        selected.find((item) => !selectedItems.includes(item));

      if (newSelected) {
        props.filterRangeProps.handleChange(newSelected);
      }
    },
    [selected]
  );

  return (
    <ChoiceList
      title={props.filter.name}
      onChange={handleOptionChange}
      allowMultiple={"multi" === props.filterRangeProps.filter.displayType}
      choices={props.filterRangeProps.options.map(({ key, label, checked }) => ({
        value: key,
        label: label,
        active: checked,
      }))}
      selected={selected}
    />
  );
}
